import React, { useState, FormEvent } from "react";
import "./Login.scss";

type LoginResponse = {
  status?: boolean;
  message?: string;
  data?: {
    access_token?: string;
    refresh_token?: string;
    user?: {
      user_id: string;
      first_name: string;
      last_name: string;
      email: string;
      role_id: number;
    };
  } | null;
  error?: string | Record<string, any> | null;
};

export default function Login() {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = (showErrors = true): boolean => {
    const next: Record<string, string> = {};

    if (!email.trim()) next.email = "Email is required.";
    else if (!emailRegex.test(email)) next.email = "Enter a valid email address.";

    if (!password) next.password = "Password is required.";

    if (showErrors) setErrors(next);

    const valid = Object.keys(next).length === 0;
    if (!valid && showErrors) setErrorMsg("Please fix the highlighted errors.");
    else setErrorMsg(null);

    return valid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);

    if (!validate()) return;

    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
        }),
        credentials: "include",
      });

      let json: LoginResponse;
      try {
        json = await res.json();
      } catch {
        throw new Error("Server returned invalid response");
      }

      if (res.ok && json.status === true) {
        setSuccessMsg(json.message || "Login successful");

        setTimeout(() => {
          window.location.href = "/";
        }, 1000);
      } else {
        const serverMessage =
          (typeof json.error === "string" && json.error) ||
          json.message ||
          "Login failed. Please check your credentials.";

        setErrorMsg(serverMessage);
      }
    } catch (err: any) {
      console.error("Login network error:", err);
      setErrorMsg("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleSubmit} noValidate>
        <div className="heading-section">
          <h2>Login</h2>
          <p className="subtext">Welcome back! Please sign in to continue.</p>
        </div>

        {successMsg && <div className="success">{successMsg}</div>}
        {errorMsg && <div className="error">{errorMsg}</div>}

        <div className="input-group">
          <label>Email</label>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => {
                const copy = { ...prev };
                delete copy.email;
                return copy;
              });
            }}
          />
          {errors.email && <div className="field-error">{errors.email}</div>}
        </div>

        <div className="input-group">
          <label>Password</label>
          <input
            type="password"
            placeholder="Your password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((prev) => {
                const copy = { ...prev };
                delete copy.password;
                return copy;
              });
            }}
          />
          {errors.password && (
            <div className="field-error">{errors.password}</div>
          )}
        </div>

        <button className="primary-btn" type="submit" disabled={loading}>
          {loading ? "Checking..." : "Login"}
        </button>
      </form>
    </div>
  );
}
